import React, { useEffect, useState, useCallback } from "react";
import { NavLink, Outlet, useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import "./Portal.css";
import { setLogout, setUser } from "../../redux/features/authSlice";
import { me, getNotifications } from "../../services/authService";
import { getOrganization } from "../../services/orgService";

// The customer portal shell. Top bar with the brand, the organization and
// the notifications bell; content; a bottom nav on phones that becomes a
// top row on wider screens. Everything inside is the signed-in user's own
// organization - the server scopes it, the shell just shows the name.

export const OrgContext = React.createContext(null);

const NAV = [
  { to: "/portal", label: "Home", end: true, icon: "M8 1.5 1.5 7v7.5h4.5v-4h4v4h4.5V7L8 1.5z" },
  { to: "/portal/plants", label: "Plants", icon: "M8 14s5-2.5 5-7c0-2-1.5-4-5-4S3 5 3 7c0 4.5 5 7 5 7zm0-9a2 2 0 1 1 0 4 2 2 0 0 1 0-4z" },
  { to: "/portal/list", label: "My list", icon: "M2 3h2v2H2V3zm3 0h9v2H5V3zM2 7h2v2H2V7zm3 0h9v2H5V7zm-3 4h2v2H2v-2zm3 0h9v2H5v-2z" },
  { to: "/portal/requirements", label: "Requirements", icon: "M3 2h10v12H3V2zm2 3h6v1H5V5zm0 3h6v1H5V8zm0 3h4v1H5v-1z" },
  { to: "/portal/quotations", label: "Quotes", icon: "M2 3h12v10H2V3zm1 2v1h10V5H3zm0 3v1h7V8H3zm0 3v1h5v-1H3z" },
  { to: "/portal/orders", label: "Orders", icon: "M1 4h3l1.5 7h7L14 6H5.2M6 14a1 1 0 1 0 0-2 1 1 0 0 0 0 2zm6 0a1 1 0 1 0 0-2 1 1 0 0 0 0 2z" },
  { to: "/portal/account", label: "Account", icon: "M2 3h12v10H2V3zm1 2v1h10V5H3zm0 3v1h6V8H3zm7 3h3v1h-3v-1z" },
  { to: "/portal/more", label: "More", icon: "M3 7h2v2H3V7zm4 0h2v2H7V7zm4 0h2v2h-2V7z" },
];

const PortalLayout = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { user } = useSelector((s) => s.auth);
  const [org, setOrg] = useState(null);
  const [unread, setUnread] = useState(0);

  const refreshOrg = useCallback(async () => {
    if (!user?.organizationId) return;
    try {
      setOrg(await getOrganization(user.organizationId));
    } catch (e) {
      /* the shell still renders; pages show their own errors */
    }
  }, [user?.organizationId]);

  useEffect(() => {
    // Re-read who we are from the server: role or org may have changed
    // since the token was issued, and the server is what enforces it.
    me().then((u) => dispatch(setUser({ ...user, ...u }))).catch(() => {
      dispatch(setLogout());
      localStorage.clear();
      navigate("/");
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    refreshOrg();
  }, [refreshOrg]);

  useEffect(() => {
    let alive = true;
    const poll = async () => {
      try {
        const rows = await getNotifications(true);
        if (alive) setUnread(rows.length);
      } catch (e) {
        /* ignore */
      }
    };
    poll();
    const t = setInterval(poll, 60000);
    return () => { alive = false; clearInterval(t); };
  }, []);

  return (
    <OrgContext.Provider value={{ org, refreshOrg, user }}>
      <div className="portal">
        <header className="portal-top">
          <div>
            <div className="brand">HortiHub</div>
            <div className="org">{org?.name || " "}</div>
          </div>
          <button className="bell" aria-label="Notifications" onClick={() => navigate("/portal/notifications")}>
            <svg width="22" height="22" viewBox="0 0 16 16" fill="currentColor"><path d="M8 16a2 2 0 0 0 2-2H6a2 2 0 0 0 2 2zm.995-14.901a1 1 0 1 0-1.99 0A5.002 5.002 0 0 0 3 6c0 1.098-.5 6-2 7h14c-1.5-1-2-5.902-2-7 0-2.42-1.72-4.44-4.005-4.901z" /></svg>
            {unread > 0 && <span className="badge rounded-pill bg-danger">{unread}</span>}
          </button>
        </header>
        <nav className="portal-nav">
          {NAV.map((n) => (
            <NavLink key={n.to} to={n.to} end={n.end} className={({ isActive }) => (isActive ? "active" : "")}>
              <svg viewBox="0 0 16 16" fill="currentColor"><path d={n.icon} /></svg>
              <span>{n.label}</span>
            </NavLink>
          ))}
        </nav>
        <main className="portal-main">
          <Outlet />
        </main>
      </div>
    </OrgContext.Provider>
  );
};

export default PortalLayout;
